/* eslint-disable no-useless-computed-key */
import React from 'react';
import AppRoutes from './routes/appRoutes';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { I18nextProvider } from 'react-i18next';
import { SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router-dom';
import { persistor, store } from './store';
import i18n from './config/i18n';
import ScrollToTop from './components/scrollToTop';
import { configureInitialInterceptors } from './api/client';
import ReactGA from 'react-ga4';
import TrackPageView from './routes/appRoutes/TrackPageView';
import TagManager from 'react-gtm-module';
configureInitialInterceptors(store);

const queryClient = new QueryClient();
const theme = createTheme({
  palette: {
    primary: {
      main: '#1a4861',
      dark: '#093147',
      light: '#9DABB3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0093ca',
      dark: '#044f85',
      light: 'rgba(0,147,202,0.15)',
      contrastText: '#ffffff',
    },

    background: {
      default: '#ffffff',
      paper: '#ffffff',
      alt: '#1a4861',
      altDark: '#1a4861',
    },

    text: {
      primary: '#616973',
      secondary: '#7A828C',
      muted: '#bbbbbb',
    },

    divider: '#e4e6f1',
    footer: {
      bg: 'rgba(58,67,84,0.09)',
      border: '#dce7f7',
    },
    success: {
      main: '#3BA56D',
      dark: '#2B7B50',
      light: '#3BA56D26',
      contrastText: '#ffffff',
    },

    error: {
      main: '#D64545',
      dark: '#B13434',
      light: '#D6454526',
      contrastText: '#ffffff',
    },

    warning: {
      main: '#F2A100',
      dark: '#C77F00',
      light: '#F2A10026',
      contrastText: '#1A1C1E',
    },
  },

  typography: {
    fontFamily: ['Plus Jakarta Sans', 'sans-serif'].join(','),

    // ----------------------------
    // ORIGINAL TYPOGRAPHY KEEPED
    // ----------------------------

    h1: {
      fontSize: '38px',
      fontWeight: '900',
      lineHeight: '48px',
      ['@media (min-width:600px)']: {
        fontSize: '72px',
        lineHeight: '94px',
      },
    },

    title: {
      fontSize: '38px',
      fontWeight: '900',
      letterSpacing: '-2.16px',
      fontFamily: 'Plus Jakarta Sans',
      fontVariantLigatures: 'common-ligatures',
      lineHeight: '48px',
      ['@media (min-width:600px)']: {
        fontSize: '72px',
        lineHeight: '94px',
      },
    },

    h2: {
      fontSize: '38px',
      fontWeight: '700',
      lineHeight: '48px',
      ['@media (min-width:600px)']: {
        fontSize: '56px',
        lineHeight: '72px',
      },
    },

    h3: {
      fontSize: '1.75rem',
      fontWeight: '700',
      lineHeight: '78px',
      letterSpacing: '-0.03em',
      ['@media (min-width:600px)']: {
        fontSize: '56px',
        lineHeight: '48px',
      },
    },

    h4: {
      fontSize: '1.75rem',
      fontWeight: '700',
      lineHeight: '27px',
      letterSpacing: '-0.03em',
      color: '#1A1C1E',
      ['@media (min-width:600px)']: {
        fontSize: '32px',
        lineHeight: '48px',
      },
    },

    h5: {
      fontSize: '1.50rem',
      fontWeight: '700',
      lineHeight: '48px',
      color: '#1A1C1E',
      ['@media (min-width:600px)']: {
        fontSize: '28px',
      },
    },

    h6: {
      fontSize: '1.50rem',
      fontWeight: '700',
      lineHeight: '36px',
      letterSpacing: '-0.03em',
      color: '#1A1C1E',
      ['@media (min-width:600px)']: {
        fontSize: '24px',
      },
    },

    body1: {
      fontWeight: '400',
      fontSize: '16px',
      lineHeight: '27px',
      letterSpacing: '-0.02em',
      ['@media (min-width:600px)']: {
        letterSpacing: '-0.02em',
        fontSize: '14px',
        lineHeight: '27px',
      },
    },

    secondary: {
      fontWeight: '400',
      fontSize: '16px',
      color: '#6C7278',
      lineHeight: '150% !important',
      letterSpacing: '-0.02em',
      ['@media (min-width:600px)']: {
        letterSpacing: '-0.02em',
        fontSize: '18px',
      },
    },

    body2: {
      color: '#1A1C1E',
      fontSize: '16px',
      fontWeight: '400',
      letterSpacing: '-0.02em',
    },

    subtitle1: {
      color: '#6C7278',
      fontSize: '14px',
      fontWeight: '400',
    },

    button: {
      fontFamily: 'Manrope',
      fontSize: '16px',
      fontWeight: '400',
      letterSpacing: '-0.02em',
      color: '#1A1C1E',
    },
  },
});

ReactGA.initialize(process.env.REACT_APP_GOOGLE_TRACKING_ID);

const tagManagerArgs = {
  gtmId: process.env.REACT_APP_GOOGLE_TAG_ID,
};

TagManager.initialize(tagManagerArgs);

function App() {
  window.dataLayer.push({
    event: 'pageview',
  });
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
              <SnackbarProvider>
                <BrowserRouter>
                  <ScrollToTop>
                    <div className="App">
                      <CssBaseline />
                      <TrackPageView />
                      <AppRoutes />
                    </div>
                  </ScrollToTop>
                </BrowserRouter>
              </SnackbarProvider>
            </ThemeProvider>
          </I18nextProvider>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
